/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { BtrEventModel } from '@app/core/models';
import { SnackbarService, StorageService } from '@app/core/services';
import { BtrEventApiService } from '@app/shared/services/api';

const TSTATE_KEY = makeStateKey<BtrEventModel>('event-resolver');

@Injectable()
export class EventResolver implements Resolve<BtrEventModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly btrEventApiService: BtrEventApiService,
    private readonly transferState: TransferState,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly storageService: StorageService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<BtrEventModel> | Promise<BtrEventModel> | BtrEventModel {
    const slug = route.paramMap.get('eventSlug');
    return this.isServer
      ? this.serverResolve(slug, route)
      : this.browserResolve(slug, route);
  }

  private browserResolve(
    slug: string,
    route: ActivatedRouteSnapshot,
  ): Observable<BtrEventModel> | BtrEventModel {
    const tmp = this.transferState.get<
      Pick<BtrEventModel, keyof BtrEventModel>
    >(TSTATE_KEY, null);

    if (tmp != null) {
      if (!tmp.showAlone && route.data?.page === ROUTING_KEY.STANDALONE_EVENT) {
        this.router.navigate(['events', tmp.slug]);
      }
      if (
        !tmp.showInWhiteLabel &&
        tmp.showAlone &&
        route.data?.page !== ROUTING_KEY.STANDALONE_EVENT
      ) {
        this.router.navigate(['s/event', tmp.slug]);
      }
      this.transferState.remove<BtrEventModel>(TSTATE_KEY);
      return BtrEventModel.copyObj(tmp);
    }
    if (this.storageService.EVENT?.slug === slug) {
      const event = this.storageService.EVENT;
      if (
        !event.showAlone &&
        route.data?.page === ROUTING_KEY.STANDALONE_EVENT
      ) {
        this.router.navigate(['events', event.slug]);
      }
      if (
        !event.showInWhiteLabel &&
        event.showAlone &&
        route.data?.page !== ROUTING_KEY.STANDALONE_EVENT
      ) {
        this.router.navigate(['s/event', event.slug]);
      }
      return event;
    }
    return this.btrEventApiService.getEvent({ slug }, true).pipe(
      tap(data => {
        if (
          !data.showAlone &&
          route.data?.page === ROUTING_KEY.STANDALONE_EVENT
        ) {
          this.router.navigate(['events', data.slug]);
        }
        if (
          !data.showInWhiteLabel &&
          data.showAlone &&
          route.data?.page !== ROUTING_KEY.STANDALONE_EVENT
        ) {
          this.router.navigate(['s/event', data.slug]);
        }
      }),
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }

  private serverResolve(
    slug: string,
    route: ActivatedRouteSnapshot,
  ): Observable<BtrEventModel> {
    return this.btrEventApiService.getEvent({ slug }, true).pipe(
      tap(data => {
        this.transferState.set<BtrEventModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }
}
